import { History as IHistory, createHashHistory, LocationListener, LocationState } from 'history';
import { Logger } from '../support/Logger';
import { State } from '@meraki-internal/state';

/**
 * NOTE: if we ever want to "hook" into all history changes, our proxy methods are not
 *          where to do it. Instead hook into / subscribe to this.history  for that.
 *          eg our proxy methods won't know about a <a href="/cool" /> click
 */
export class HistoryViewModel extends State {
    history: IHistory<unknown>;

    private initialLength = 0;
    private currentLength = 0;

    static inject = () => [Logger];
    constructor(log: Logger) {
        super({});

        this.history = createHashHistory();
        this.initialLength = this.history.length;
        this.currentLength = this.history.length;

        this.history.listen((loc, action) => {
            if (action === 'PUSH') {
                this.currentLength++;
            }
            if (action === 'POP') {
                // rapid page reloads during test setup can cause bogus events to be
                // received, so don't let a POP push us back past the initial length
                if (this.currentLength > this.initialLength) {
                    this.currentLength--;
                }
            }
            log.info('Navigated', loc);
            this.setState({});
        });
    }

    getLength = () => this.currentLength;

    getRawHistory = () => this.history;

    push = (path: string) => {
        this.history.push(path);
    };

    replace = (path: string) => {
        // if there is a hash, remove it
        if (path && path[0] === '#'){
            path = path.substring(1);
        }
        this.history.replace(path);
    };

    back = ({ pages = 1, fallbackPath }: { pages?: number, fallbackPath?: string } = {}) => {
        if (pages < 1) {
            throw new Error('back pages must be >= 1');
        }
        if (this.currentLength - pages < this.initialLength) {
            // the app may have started from a deep link to a page that has a back button,
            // in which case the user may trigger history.back() when there is no hisory,
            // but we don't want to go back to a page outside the app, so in this case
            // just replace the current page with the root path (to return to the map)
            this.replace(fallbackPath || '/');
        }
        else {
            this.history.goBack();

            // ideally we'd use history.go(-pages) but ionic doesn't support this;
            // it updates the url correctly, but renders the first page in history
            // (see https://github.com/ionic-team/ionic-framework/issues/23775);
            // the only thing that seems to work is multiple calls to goBack()
            // and we need timeouts between them to get it to work headless
            if (pages > 1) {
                setTimeout(() => this.back({ pages: pages - 1, fallbackPath }), 100);
            }
        }
    };

    // returns [[k1: v1], [k2, v2]]
    getCurrentSearchParams = (): URLSearchParams => {
        return new URLSearchParams(this.history.location.search || '?');
    };

    // returns {k1: v2, k2: v2}
    getCurrentSearchParamMap = (): any => {
        return Array.from(this.getCurrentSearchParams().entries()).reduce((m, [k, v]) => ({ ...m, [k]: v }), {});
    };

    removeSearchParam = (key: string) => {
        const params = this.getCurrentSearchParams();
        if (params.has(key)) {
            params.delete(key);
            this.push(`${this.history.location.pathname}?${params}`);
        }
    };

    getCurrentLocation = () => {
        return this.history.location;
    };

    listen = (listener: LocationListener<LocationState>) => this.history.listen(listener);

    popSearchParam = ({ key }: { key: string }) => {
        const params = this.getCurrentSearchParams();
        const value = params.get(key);
        this.removeSearchParam(key);
        return value === null ? undefined : value;
    };

}
